import { RefObject, useEffect, useRef } from "react"
import { createGroupIconFunction, createMarkerIcon } from "./marker-icon"
import { createBookingLink, createBookingLinkParams } from "../booking/booking-util"
import { postBookingLinkClick } from "../analytics"
import L from "leaflet"
import "leaflet.markercluster"
import { decodePhotoUrl } from "../../utils/photo_url"
import { IBookingHotel } from "../../shared-interfaces/booking-hotel"

// do not collapse empty tags! - this will break the leaflet template parser.
const template = `
  <div class="map-y-widget gyg">
    <div class="map-y-picture-container">
      <div class="map-y-picture" style="{largerImageBgUrl}">
        <div class="map-y-hotel-stars">
          <div class="map-y-stars {starsCssClass}"></div>
        </div>
      </div>
    </div>
    <div class="map-y-text-container">
      <h4 class="map-y-title" tabindex="0">
        <span class="map-y-title-text">{name}</span>
      </h4>
      <div class="map-y-bookig-pseudolink"><i class="icon event_available"></i><span>Verfügbarkeit auf Booking.com prüfen</span></div>
      <div class="map-y-rating-price-table">
        <div class="map-y-rating-price-column {cssClassNoRating}">
          <span class="map-y-hotel-rating">{ratingStr}</span>
          <span>von 10</span>
        </div>
        <div class="map-y-rating-price-column">
            <span class="map-y-price">
              <span class="map-y-price-from">Ab</span> 
              <span class="map-y-price-min">{minimumPriceStr}&nbsp;€</span> 
           </span>
      </div>
    </div>
    <a
      href="{url}"
      tabindex="0" target="_blank" class="map-y-link">
      <span class="map-y-link-text">Details ansehen</span></a>
  </div>
`

const ratingFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
})

export const useAccosEffect = (
  accos: IBookingHotel[] | null,
  mapVisible: boolean,
  showPrice: boolean,
  map: RefObject<L.Map>,
  trackName: string,
  tourSlug: string) => {

  const markers = useRef<L.MarkerClusterGroup | null>(null)

  useEffect(() => {

    if (markers.current && map.current) {
      map.current.removeLayer(markers.current)
    }

    if (mapVisible && accos) {

      markers.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        showCoverageOnHover: false,
        iconCreateFunction: createGroupIconFunction(showPrice, "hotel"),
      })

      accos.forEach((acco: any) => {

        const smallImageUrl = decodePhotoUrl(acco.p_url).replace("max500", "max200")

        const priceInfo = {
          price: acco.minrate,
          minimumPriceStr: acco.minrate ? acco.minrate + "" : "?",
        }

        const smallImageBgUrl = `background-image: url(${smallImageUrl})`

        const marker = L.marker(acco.latLng, {
          // @ts-ignore
          priceInfo, // cluster marker needs this
          smallImageBgUrl, // cluster marker needs this
          acco,
          zIndexOffset: 0,
          icon: createMarkerIcon(showPrice, "hotel", smallImageBgUrl, priceInfo),
        })

        marker.bindPopup((currentMarker: any) => {
          const options = currentMarker.options
          const popupAcco = options.acco
          const popupPriceInfo = options.priceInfo

          const popupContent = L.Util.template(template, {
            largerImageBgUrl: `background-image: url(${decodePhotoUrl(popupAcco.p_url)})`,
            name: popupAcco.name,
            ratingStr: popupAcco.rating ? ratingFormatter.format(popupAcco.rating) : "",
            minimumPriceStr: popupPriceInfo.minimumPriceStr,
            url: createBookingLink(popupAcco.hotel_url, trackName),
            starsCssClass: popupAcco.stars ? ("map-y-hotel-stars" + popupAcco.stars) : "",
            cssClassNoRating: popupAcco.rating ? "" : "map-y-no-rating",
          })

          const domPopupContent = new DOMParser().parseFromString(popupContent, "text/html").body.childNodes[0] as HTMLElement

          domPopupContent.querySelector("a")!.addEventListener("click", () => {
            const bookingLinkParams = createBookingLinkParams(popupAcco, tourSlug)
            postBookingLinkClick(tourSlug, bookingLinkParams)
          })

          return domPopupContent
        }, {
          className: "leaflet-popup-yovelino",
          maxWidth: 236,
          minWidth: 236,
        }).openPopup()

        markers.current!.addLayer(marker)
      })

      map.current!.addLayer(markers.current)
    }

  }, [accos, mapVisible, showPrice, map, trackName, tourSlug])
}
